/**
 * Token storage + cross-tab coordination.
 *
 * Access token: module MEMORY only (never persisted — F5 restore goes
 * through the refresh grant). Refresh token: localStorage — the single
 * cross-tab source of truth, required for the rotation-race fix below.
 *
 * THE ROTATION RACE: refresh tokens rotate on every use and reuse revokes
 * the whole family. Two tabs refreshing concurrently would kill the session.
 * Fix: refresh() serializes across tabs via navigator.locks; inside the
 * lock the loser re-checks "already refreshed by another tab" and re-reads
 * the rotated token from localStorage — it never replays a spent token.
 */
import { refreshGrant } from "./oauthService";

const RT_KEY = "auth.refreshToken";
const ROTATED_AT_KEY = "auth.rtRotatedAt";
const LOGOUT_KEY = "auth.logout";
const CHANNEL = "overture-auth";
const EXPIRY_SKEW_MS = 5000;
const RECENT_ROTATION_MS = 30000;

let _accessToken = null;
let _expiresAt = 0; // ms epoch
let _refreshInFlight = null;
const _listeners = new Set();

const channel =
  typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(CHANNEL) : null;

function emit(event) {
  _listeners.forEach((fn) => {
    try {
      fn(event);
    } catch {
      /* listener errors never break auth */
    }
  });
}

if (channel) {
  channel.onmessage = (msg) => {
    const event = msg.data;
    if (event?.type === "token") {
      _accessToken = event.accessToken;
      _expiresAt = event.expiresAt;
    }
    // `remote` tells listeners the token came from ANOTHER tab: a signed-out
    // tab receiving one has no identity for it and must hydrate (auth-hook),
    // whereas a local setSession() is always followed by the caller's login().
    emit({ ...event, remote: true });
  };
}
if (typeof window !== "undefined") {
  // storage-event fallback: BroadcastChannel doesn't fire in the emitting
  // tab, storage doesn't fire in the writing tab — together they cover all.
  window.addEventListener("storage", (e) => {
    if (e.key === LOGOUT_KEY && e.newValue) {
      _accessToken = null;
      _expiresAt = 0;
      emit({ type: "logout" });
    }
  });
}

/** Documented accessor for long polling loops — always the current token. */
export function getAccessToken() {
  return _accessToken;
}

export function isExpired() {
  return !_accessToken || Date.now() >= _expiresAt;
}

export function getExpiresAt() {
  return _expiresAt;
}

export function hasRefreshToken() {
  return !!localStorage.getItem(RT_KEY);
}

export function setSession(tokens) {
  _accessToken = tokens.access_token;
  _expiresAt = Date.now() + (tokens.expires_in || 900) * 1000 - EXPIRY_SKEW_MS;
  if (tokens.refresh_token) {
    localStorage.setItem(RT_KEY, tokens.refresh_token);
    localStorage.setItem(ROTATED_AT_KEY, String(Date.now()));
  }
  const event = { type: "token", accessToken: _accessToken, expiresAt: _expiresAt };
  channel?.postMessage(event);
  emit(event);
}

export function clearSession() {
  _accessToken = null;
  _expiresAt = 0;
  localStorage.removeItem(RT_KEY);
  localStorage.removeItem(ROTATED_AT_KEY);
}

/** Broadcast logout to every tab (callers do their own local cleanup). */
export function broadcastLogout() {
  channel?.postMessage({ type: "logout" });
  localStorage.setItem(LOGOUT_KEY, String(Date.now()));
}

export function onAuthEvent(handler) {
  _listeners.add(handler);
  return () => _listeners.delete(handler);
}

function recentlyRotated() {
  const at = Number(localStorage.getItem(ROTATED_AT_KEY) || 0);
  return Date.now() - at < RECENT_ROTATION_MS;
}

/** Cross-tab mutex: navigator.locks, with a same-tab fallback shim. */
function withLock(fn) {
  if (typeof navigator !== "undefined" && navigator.locks?.request) {
    return navigator.locks.request("overture-token-refresh", fn);
  }
  return fn(); // degraded: in-tab single-flight still applies
}

/**
 * Refresh the session. In-tab single-flight + cross-tab lock. Throws the
 * oauthService typed errors ({code:"invalid_grant"} → session dead).
 */
export function refresh() {
  if (_refreshInFlight) return _refreshInFlight;
  _refreshInFlight = withLock(async () => {
    // Another tab may have refreshed while we waited for the lock — its
    // broadcast already handed us a fresh access token.
    if (!isExpired() && recentlyRotated()) {
      return _accessToken;
    }
    // Re-read the RT NOW (never from a captured variable) — the lock winner
    // may have rotated it.
    const rt = localStorage.getItem(RT_KEY);
    if (!rt) {
      const err = new Error("No refresh token");
      err.code = "invalid_grant";
      throw err;
    }
    const tokens = await refreshGrant(rt);
    setSession(tokens);
    return tokens.access_token;
  })
    .catch((err) => {
      if (err?.code === "invalid_grant") clearSession();
      throw err;
    })
    .finally(() => {
      _refreshInFlight = null;
    });
  return _refreshInFlight;
}

/** Test hook. */
export function _resetForTests() {
  _accessToken = null;
  _expiresAt = 0;
  _refreshInFlight = null;
  _listeners.clear();
}
